import { BrowserHistoryArgs } from 'instantsearch.js/es/lib/routers/history';
import * as qs from 'qs';
import { normalizeRefinements } from '../algolia-helpers';
import { BaseRouteState, PathInfo, RouterQueryStringParams, RoutingConfig } from './types';

/**
 * Router configuration for journal pages with path-based and query string routing.
 *
 * If no path-based routing is used, any refinement list can become a path-based filter.
 * Once a path filter is set, all other refinement lists become query string parameters.
 * Additional values on a path filter refinement list also become query string parameters.
 *
 * Example:
 * The URL:
 *   /about/journal/technology/algolia?technology=contentful&solution=ecommerce
 * would map to the route state:
 *   {
 *     technology: ["algolia", "contentful"],
 *     solution: ["ecommerce"]
 *   }
 *
 */
export default function generateRouter<U extends string>({
    enabledRefinementLists,
    defaultRouteState,
    basePath,
}: RoutingConfig<U>): Partial<BrowserHistoryArgs<BaseRouteState>> {
    return {
        createURL({ qsModule, routeState, location }) {
            const pathInfo = determinePathRefinement(routeState, location, enabledRefinementLists, basePath);
            const queryStringParams = buildQueryStringParams(routeState, pathInfo, enabledRefinementLists);
            const pathname = buildPathname(pathInfo, basePath);
            const queryString = qsModule.stringify(queryStringParams, {
                arrayFormat: 'repeat',
                addQueryPrefix: true,
                encoder: urlEncodeWithPlus,
            });

            return `${location.origin}${pathname}${queryString}`;
        },

        parseURL({ qsModule, location }) {
            const routeState = parseQueryString(location, qsModule, defaultRouteState);

            if (hasPathRefinement(location, basePath)) {
                const { refinementList, value } = extractPathRefinement(location, basePath, enabledRefinementLists);
                if (refinementList) {
                    const additionalValues = routeState[refinementList];
                    const allValues = combinePathAndQueryValues(value, additionalValues);
                    routeState[refinementList] = allValues;
                }
            }

            const state = normalizeRefinements(routeState);

            return state as BaseRouteState;
        },
        cleanUrlOnDispose: false,
    };
}

/**
 * Determines which refinement list should be used for path-based routing
 */
function determinePathRefinement(
    routeState: BaseRouteState,
    location: Location,
    enabledRefinementLists: string[],
    basePath: string
): PathInfo {
    const currentPathInfo = extractPathRefinement(location, basePath, enabledRefinementLists);

    // If current URL has a path refinement and it's still in the route state, preserve it
    if (currentPathInfo.refinementList) {
        const routeStateRefinementList = routeState[currentPathInfo.refinementList];
        if (isArray(routeStateRefinementList) && routeStateRefinementList.includes(currentPathInfo.value)) {
            return currentPathInfo;
        }
    }

    // Otherwise, find the first refinement list with values
    for (const refinementList of enabledRefinementLists) {
        const values = routeState[refinementList];
        if (isArray(values) && values?.length > 0) {
            return {
                refinementList,
                value: values[0],
            };
        }
    }

    return { refinementList: null, value: '' };
}

/**
 * Builds query string parameters, excluding the path-based refinement
 */
function buildQueryStringParams(
    routeState: BaseRouteState,
    pathInfo: PathInfo,
    enabledRefinementLists: string[]
): RouterQueryStringParams {
    const queryStringParams: Record<string, string[] | number> = {};

    for (const refinementList of enabledRefinementLists) {
        const values = (routeState[refinementList] as string[]) || [];

        if (values.length === 0) continue;

        if (refinementList === pathInfo.refinementList) {
            // For path refinement, include all values except the value in the path
            const remainingValues = values.filter((v) => v !== pathInfo.value);
            if (remainingValues.length > 0) {
                queryStringParams[refinementList] = remainingValues;
            }
        } else {
            // For non-path refinements, include all values
            queryStringParams[refinementList] = values;
        }
    }

    // check for page
    if (routeState.page) {
        queryStringParams['page'] = routeState.page;
    }

    return queryStringParams as RouterQueryStringParams;
}

/**
 * Builds the pathname from path refinement info
 */
function buildPathname(pathInfo: PathInfo, basePath: string): string {
    const { refinementList, value } = pathInfo;
    const segments = [
        basePath,
        refinementList ? urlEncodeWithPlus(refinementList as string) : null,
        value ? urlEncodeWithPlus(value) : null,
    ].filter(Boolean);

    return `/${segments.join('/')}`;
}

/**
 * Parses query string parameters relevant to routing
 *
 * This enhances the qsModule.parse function to only include
 * query string parameters relevant to RouteState
 */
function parseQueryString(
    location: Location,
    qsModule: typeof qs,
    defaultRouteState: BaseRouteState
): RouterQueryStringParams {
    const allParams = qsModule.parse(location.search.slice(1));

    return Object.keys(defaultRouteState).reduce(
        (params, key) => ({
            ...params,
            [key]: allParams[key],
        }),
        {}
    ) as RouterQueryStringParams;
}

/**
 * Checks if the current location has path-based refinement
 */
function hasPathRefinement(location: Location, basePath: string): boolean {
    const noRefinementPathPattern = new RegExp(`${basePath}/?$`);
    return !noRefinementPathPattern.test(location.pathname);
}

/**
 * Extracts refinement list and value from URL path
 */
function extractPathRefinement(location: Location, basePath: string, enabledRefinementLists: string[]): PathInfo {
    if (!hasPathRefinement(location, basePath)) {
        return { refinementList: null, value: '' };
    }
    const refinementList = enabledRefinementLists.find((l) => location.pathname.includes(l));

    if (!refinementList) {
        return { refinementList: null, value: '' };
    }

    const pathSegments = location.pathname.replace(basePath, '').split('/').filter(Boolean);
    const value = pathSegments[pathSegments.indexOf(refinementList) + 1] || '';

    return { refinementList, value };
}

/**
 * Combines path value with additional query string values
 */
function combinePathAndQueryValues(pathValue: string, queryValues: unknown): string[] {
    if (typeof queryValues === 'undefined') {
        return [pathValue];
    }

    const additionalValues = Array.isArray(queryValues) ? queryValues : [queryValues].filter(Boolean);

    return [pathValue, ...additionalValues];
}

/**
 * Encodes string for use in a URL and replaces strings with "+"
 * for aesthetics
 */
function urlEncodeWithPlus(str: string) {
    return encodeURIComponent(str).replace(/%20/g, '+');
}

function isArray(value: unknown): value is string[] {
    return Array.isArray(value);
}
